import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut, Newspaper, Home, Megaphone, Upload, Link2, ImageOff, RefreshCw, FileEdit, Inbox, Settings as SettingsIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { adsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const AdSlotCard = ({ slot, onUpdate }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [savingUrl, setSavingUrl] = useState(false);
  const [url, setUrl] = useState(slot.target_url || '');
  const [active, setActive] = useState(Boolean(slot.is_active));

  useEffect(() => {
    setUrl(slot.target_url || '');
    setActive(Boolean(slot.is_active));
  }, [slot.target_url, slot.is_active]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin');
      return;
    }
    setUploading(true);
    try {
      const res = await adsAPI.uploadImage(slot.key, file);
      onUpdate(res.data);
      toast.success(`${slot.label} görseli yüklendi ve ${slot.width}x${slot.height} boyutuna otomatik uyarlandı`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Yükleme başarısız';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      const res = await adsAPI.update(slot.key, { target_url: url });
      onUpdate(res.data);
      toast.success('Hedef URL kaydedildi');
    } catch {
      toast.error('URL kaydedilemedi');
    } finally {
      setSavingUrl(false);
    }
  };

  const toggleActive = async (val) => {
    setActive(val);
    try {
      const res = await adsAPI.update(slot.key, { is_active: val });
      onUpdate(res.data);
      toast.success(val ? 'Reklam yayında' : 'Reklam duraklatıldı');
    } catch {
      setActive(!val);
      toast.error('Durum güncellenemedi');
    }
  };

  const removeImage = async () => {
    try {
      const res = await adsAPI.deleteImage(slot.key);
      onUpdate(res.data);
      toast.success('Görsel kaldırıldı');
    } catch {
      toast.error('Silinemedi');
    }
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
      data-testid={`ad-slot-card-${slot.key}`}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-gray-900">{slot.label}</h3>
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-mono rounded">
              {slot.width}×{slot.height}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{slot.location}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs font-semibold ${active ? 'text-green-600' : 'text-gray-400'}`}>
            {active ? 'Aktif' : 'Pasif'}
          </span>
          <Switch
            checked={active}
            onCheckedChange={toggleActive}
            data-testid={`ad-toggle-${slot.key}`}
          />
        </div>
      </div>

      {/* Preview area */}
      <div className="p-5">
        <div
          className="relative bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg overflow-hidden flex items-center justify-center mx-auto"
          style={{
            // Show preview at native dims, but cap width for layout
            maxWidth: `${Math.min(slot.width, 600)}px`,
            aspectRatio: `${slot.width} / ${slot.height}`,
          }}
        >
          {slot.image_data ? (
            <img
              src={slot.image_data}
              alt={slot.label}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center text-gray-400">
              <ImageOff className="w-8 h-8 mx-auto mb-1" />
              <p className="text-xs font-semibold">Görsel yüklenmedi</p>
              <p className="text-[11px]">{slot.width} × {slot.height} (otomatik uyarlanır)</p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="mt-4 grid gap-3">
          {/* Upload row */}
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid={`ad-file-input-${slot.key}`}
            />
            <Button
              type="button"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-[#0284C7] hover:bg-[#0369A1] flex-1"
              data-testid={`ad-upload-btn-${slot.key}`}
            >
              {uploading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Yükleniyor…</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> {slot.image_data ? 'Görseli Değiştir' : 'Görsel Yükle'}</>
              )}
            </Button>
            {slot.image_data && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={removeImage}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid={`ad-remove-btn-${slot.key}`}
              >
                Kaldır
              </Button>
            )}
          </div>

          {/* Target URL row */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="url"
                placeholder="https://reklam-veren-site.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="pl-10"
                data-testid={`ad-url-input-${slot.key}`}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={saveUrl}
              disabled={savingUrl}
              data-testid={`ad-url-save-${slot.key}`}
            >
              {savingUrl ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminAds = () => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin/giris');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated]);

  const load = async () => {
    try {
      const res = await adsAPI.getAll();
      setSlots(res.data || []);
    } catch (e) {
      toast.error('Reklam alanları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotUpdate = (updated) => {
    setSlots((prev) => prev.map((s) => (s.key === updated.key ? updated : s)));
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/giris');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-gray-500">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-ads">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0F172A] text-white p-4 z-50">
        <div className="mb-8">
          <h1 className="text-lg font-bold tracking-tight">HABER TÜRKİYE'DEN</h1>
          <p className="text-gray-400 text-xs mt-1">Yönetim Paneli</p>
        </div>

        <nav className="space-y-1">
          <Link
            to="/admin"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Newspaper className="w-5 h-5" />
            <span>Haberler</span>
          </Link>
          <Link
            to="/admin/talepler"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Inbox className="w-5 h-5" /><span>Gelen Talepler</span>
          </Link>
          <Link
            to="/admin/reklamlar"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 text-white"
          >
            <Megaphone className="w-5 h-5" />
            <span className="font-medium">Reklam Yönetimi</span>
          </Link>
          <Link to="/admin/ayarlar" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <SettingsIcon className="w-5 h-5" /><span>Site Ayarları</span>
          </Link>

          <div className="pt-4 pb-1">
            <p className="px-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Sayfa Yönetimi</p>
          </div>
          <Link to="/admin/sayfa/hakkimizda" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <FileEdit className="w-5 h-5" /><span>Hakkımızda</span>
          </Link>
          <Link to="/admin/sayfa/reklam-isbirligi" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <FileEdit className="w-5 h-5" /><span>Reklam & İş Birliği</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Home className="w-5 h-5" />
            <span>Siteyi Görüntüle</span>
          </Link>
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="border-t border-gray-700 pt-4 mb-4">
            <p className="text-gray-400 text-sm">{user?.username}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      <main className="ml-64 p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Reklam Yönetimi</h2>
          <p className="text-gray-500 text-sm mt-1">
            Görselleri yükleyin — sistem otomatik olarak ölçekler ve merkezler. URL ekleyin, anahtarla yayını başlatın.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-16">Yükleniyor…</div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            {slots.map((slot) => (
              <AdSlotCard
                key={slot.key}
                slot={slot}
                onUpdate={handleSlotUpdate}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAds;
